import React, { useEffect, useState } from 'react';
import tenderWiseLogo from '../../Assets/images/Frame 39059 1.png';
import { IoPrint } from 'react-icons/io5';
import apiClient from '../../Api/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';

const InvoicePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [message, setMessage] = useState(null);
  const [premiumUser, setPremiumUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(20);

  const searchParams = new URLSearchParams(location.search);
  const order_id = searchParams.get('order_id');

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        const response = await apiClient.get(
          `/user/payment/statement?order_id=${order_id}`
        );

        // console.log('payment response:', response.data);

        const {
          token: bearer,
          userDetails: { otp_status, premium_user, user_type },
        } = response.data;

        localStorage.setItem('token', bearer);
        localStorage.setItem('premium_user_i', premium_user);
        localStorage.setItem('user_type_i', user_type);

        if (response.data?.verificationResponse) {
          setInvoiceData(response.data.verificationResponse);
        } else {
          setInvoiceData([]);
        }

        setMessage(response.data?.message || 'Invoice loaded successfully.');
        setPremiumUser(premium_user); // Ensure state updates properly
      } catch (err) {
        setError('Failed to fetch invoice data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [order_id]);

  useEffect(() => {
    if (premiumUser === null) return; 
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(timer);
          if (premiumUser === 1) {
            navigate('/dashboard', { state: { fromInvoice: 'fromInvoice' } });
          } else {
            navigate('/pricingPackages');
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [premiumUser, navigate]);

  const handlePrint = () => {
    window.print();
  };

  // Redirect when countdown is clicked
  const handleRedirect = () => {
    // const isSuccess = premiumUser?.premium_user;
    if (premiumUser === 1) {
      navigate('/dashboard', { state: { fromInvoice: 'fromInvoice' } }); // Redirect to dashboard
    } else {
      navigate('/pricingPackages'); // Redirect to failure page
    }
  };

  return (
    <div className="md:p-10 p-4 min-h-screen mt-32 py-10 ">
      <h1 className="text-center font-semibold text-lg mb-4">Invoice</h1>

      <div className="flex justify-between items-center container my-2 border-b-2 pb-2 max-w-4xl mx-auto">
        {/* Countdown Display */}
        <div className="flex gap-2 text-center mt-0">
          <h1>{message}</h1>
          <span className="ml-4 text-red-500">{countdown}s</span>
          {/* Redirect Button */}
          <button
            onClick={handleRedirect}
            className="text-DefaultColor underline"
          >
            {premiumUser === 1 ? (
              <span>View Tenders</span>
            ) : (
              <span>Please try again.</span>
            )}
          </button>
        </div>

        <div onClick={handlePrint} className="cursor-pointer relative group">
          <IoPrint size={25} />
        </div>
      </div>

      {/* Additional Invoice Data Rendering */}
      {loading && <div className="text-center mt-10">Loading...</div>}
      {error && <div className="text-center mt-10 text-red-500">{error}</div>}
      <div className="mt-6">
        {/* Render invoice data here */}
        {invoiceData.length > 0 ? (
          <div
            style={{ backgroundColor: '#F7F7F7' }}
            className="max-w-4xl mx-auto bg-white shadow-md rounded-md p-6"
          >
            {/* Header Section */}
            <div className="flex flex-col md:flex-row lg:flex-row justify-between items-center mb-6">
              <div>
                <img
                  src={tenderWiseLogo}
                  alt="TableImg"
                  className="rounded-lg h-auto 2xl:w-48 md:w-44 w-36"
                />
              </div>
              <div className="text-right">
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Invoice No: {invoiceData[0]?.invoice_no || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Date: {invoiceData[0]?.date_time || 'N/A'}
                </p>
              </div>
            </div>

            {/* Customer Info */}
            <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b-2">
              Invoice To:
            </h3>
            <div className="mb-6 flex flex-col md:flex-row lg:flex-row justify-between">
              <div>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Name: {invoiceData[0]?.name || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Phone: {invoiceData[0]?.phone_no || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Email: {invoiceData[0]?.email || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Address: {invoiceData[0]?.address || 'N/A'}
                </p>
              </div>
              <div>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Account Number: {invoiceData[0]?.card_number || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Transaction Id: {invoiceData[0]?.bank_trx_id || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Order Id: {invoiceData[0]?.order_id || 'N/A'}
                </p>
                <p className="md:text-base lg:text-lg text-sm text-gray-600">
                  Transaction Status: {invoiceData[0]?.bank_status || 'N/A'}
                </p>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 pb-1 mb-1">
                Payment Summary:
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">
                        Bank Status
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center">
                        Payable Amount
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center">
                        Received Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.map((payment, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2">
                          {payment.method || 'N/A'}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {/* {payment.payable_amount || '0.00'} */}
                          {Number(payment.payable_amount || 0).toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {/* {payment.sp_message === 'cancelled' ||
                          payment.sp_message === 'Failed'
                            ? '0.00'
                            : payment.recived_amount || '0.00'} */}
                          {Number(
                            payment.sp_message === 'cancelled' ||
                              payment.sp_message === 'Failed'
                              ? 0
                              : payment.recived_amount || 0
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Total Section */}
                  {invoiceData.map((discount, index) => (
                    <tfoot>
                      <tr>
                        <td
                          className="px-4 py-2 text-right font-medium"
                          colSpan="2"
                        >
                          Sub Total:
                        </td>
                        {/* <td className="px-4 py-2 text-center">{discount.amount}</td>
                         */}
                        <td className="px-4 py-2 text-center  text-gray-800">
                          {/* {discount.sp_message === 'cancelled' ||
                          discount.sp_message === 'initiated' ||
                          discount.sp_message === 'Failed'
                            ? '0.00'
                            : discount.amount || '0.00'} */}
                          {Number(
                            discount.sp_message === 'cancelled' ||
                              discount.sp_message === 'initiated' ||
                              discount.sp_message === 'Failed'
                              ? 0
                              : discount.amount || 0
                          ).toFixed(2)}
                        </td>
                      </tr>

                      <tr>
                        <td
                          className="px-4 py-2 text-right font-medium"
                          colSpan="2"
                        >
                          Discount Percentage:
                        </td>
                        <td className="px-4 py-2 text-center">
                          {discount.disc_percent}%
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="px-4 py-2 text-right font-medium"
                          colSpan="2"
                        >
                          Discount Amount:
                        </td>
                        <td className="px-4 py-2 text-center">
                          {/* {discount.discount_amount} */}
                          {Number(discount.discount_amount || 0).toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="px-4 py-2 text-right  text-gray-800"
                          colSpan="2"
                        >
                          Grand Total:
                        </td>
                        <td className="px-4 py-2 text-center text-gray-800">
                          {/* {discount.sp_message === 'cancelled' ||
                          discount.sp_message === 'Failed'
                            ? '0.00'
                            : discount.recived_amount || '0.00'} */}
                          {discount.sp_message === 'cancelled' ||
                          discount.sp_message === 'Failed'
                            ? '0.00'
                            : Number(discount.recived_amount || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  ))}
                </table>
              </div>
            </div>

            {invoiceData[0]?.method === 'CARD' && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 pb-1 mb-1">
                  Payment Summary:
                </h3>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 text-sm">
                    {/* Separator Line with "Card Section" */}

                    {/* Card Payment Table */}
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          Card Holder Name
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          USD amount
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-left">
                          USD Rate
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-center">
                          Payable Amount
                        </th>
                        <th className="border border-gray-300 px-4 py-2 text-center">
                          Received Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.map((payment, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2">
                            {payment.card_holder_name || 'N/A'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {payment.usd_amt || '0.00'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {payment.usd_rate || '0.00'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            {payment.sp_message === 'cancelled' ||
                            payment.sp_message === 'initiated' ||
                            payment.sp_message === 'Failed'
                              ? '0.00'
                              : payment.recived_amount || '0.00'}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    {/* Total Section */}
                    {invoiceData.map((discount, index) => (
                      <tfoot>
                        <tr>
                          <td
                            className="px-4 py-2 text-right font-medium"
                            colSpan="2"
                          >
                            Sub Total:
                          </td>
                          <td className="px-4 py-2 text-center  text-gray-800">
                            {discount.sp_message === 'cancelled' ||
                            discount.sp_message === 'Failed'
                              ? '0.00'
                              : discount.amount || '0.00'}
                          </td>
                        </tr>

                        <tr>
                          <td
                            className="px-4 py-2 text-right font-medium"
                            colSpan="2"
                          >
                            Discount Percentage:
                          </td>
                          <td className="px-4 py-2 text-center">
                            {discount.disc_percent}%
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="px-4 py-2 text-right font-medium"
                            colSpan="2"
                          >
                            Discount Amount:
                          </td>
                          <td className="px-4 py-2 text-center">
                            {discount.discount_amount}
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="px-4 py-2 text-right  text-gray-800"
                            colSpan="2"
                          >
                            Grand Total:
                          </td>
                          <td className="px-4 py-2 text-center text-gray-800">
                            {discount.sp_message === 'cancelled' ||
                            discount.sp_message === 'Failed'
                              ? '0.00'
                              : discount.recived_amount || '0.00'}
                          </td>
                        </tr>
                      </tfoot>
                    ))}
                  </table>
                </div>
              </div>
            )}

            {/* Footer Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b-2">
                Thank You:
              </h3>
              <div className="text-center mt-2">
                <p className="md:text-base lg:text-lg text-sm text-gray-600 font-medium">
                  Invoice was created on {invoiceData[0]?.date_time || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center">No invoice data found.</p>
        )}
      </div>
    </div>
  );
};

export default InvoicePage;

// import React, { useEffect, useState } from 'react';
// import tenderWiseLogo from '../../Assets/images/Frame 39059 1.png';
// import { IoPrint } from 'react-icons/io5';
// import apiClient from '../../Api/apiClient';
// import { useLocation, useNavigate } from 'react-router-dom';

// const InvoicePage = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [invoiceData, setInvoiceData] = useState([]);
//   const [message, setMessage] = useState(null);
//   const [premiumUser, setPremiumuser] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const searchParams = new URLSearchParams(location.search);
//   const order_id = searchParams.get('order_id');

//   useEffect(() => {
//     const fetchInvoiceData = async () => {
//       try {
//         const response = await apiClient.get(
//           `/user/payment/statement?order_id=${order_id}`
//         );

//         console.log('payment response:', response.data);

//         const {
//           token: bearer,
//           userDetails: { otp_status, premium_user, user_type },
//         } = response.data;
//         console.log('otp_status:', otp_status);
//         console.log('premium_user:', premium_user);
//         console.log('user_type:', user_type);

//         localStorage.setItem('token', bearer);
//         localStorage.setItem('premium_user_i', premium_user);
//         localStorage.setItem('user_type_i', user_type);

//         if (response.data?.verificationResponse) {
//           setInvoiceData(response.data.verificationResponse);
//         } else {
//           setInvoiceData([]);
//         }
//         setMessage(response.data?.message || 'Invoice loaded successfully.');

//         if (response.data?.userDetails) {
//           setPremiumuser(response.data?.userDetails || '');
//         } else {
//           setPremiumuser(0);
//         }
//       } catch (err) {
//         setError('Failed to fetch invoice data');
//         console.error(err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchInvoiceData();
//   }, [order_id]);

//   const handlePrint = () => {
//     window.print(); // This will open the print dialog
//   };

//   // Set up a countdown timer
//   const [countdown, setCountdown] = useState(15);
//   const isSuccess = premiumUser?.premium_user;

//   console.log('isSuccess', isSuccess);

//   useEffect(() => {
//     // Only start the countdown when the component is mounted
//     const timer = setInterval(() => {
//       setCountdown(prev => {
//         if (prev === 1) {
//           // Redirect when the countdown reaches 1 second
//           clearInterval(timer);
//           if (isSuccess === 1) {
//             navigate('/dashboard'); // Redirect to success page
//           } else {
//             navigate('/pricingPackages'); // Redirect to failure page
//           }
//         }
//         return prev - 1;
//       });
//     }, 1000); // Update every second

//     // Cleanup timer when component unmounts
//     return () => clearInterval(timer);
//   }, [isSuccess, navigate]);

// // Redirect when countdown is clicked
// const handleRedirect = () => {
//   const isSuccess = premiumUser?.premium_user;

//   if (isSuccess === 1) {
//     navigate('/dashboard'); // Redirect to home page
//   } else {
//     navigate('/pricingPackages'); // Redirect to failure page
//   }
// };

//   return (
//     <div className="md:p-10 p-4 min-h-screen mt-32 py-10 ">
//       <h1 className="text-center font-semibold text-lg mb-4">Invoice</h1>

//       <div className="flex justify-between items-center container my-2 border-b-2 pb-2 max-w-4xl mx-auto">
//         {/* Countdown Display */}
//         <div className="flex gap-2 text-center mt-0">
//           <h1>{message}</h1>
//           <span className="ml-4 text-red-500">{countdown}s</span>
//           {/* Redirect Button */}
//           <button
//             onClick={handleRedirect}
//             className="text-DefaultColor underline"
//           >
//             {message === 'Payment Canceled.' ? (
//               <span>Please try again.</span>
//             ) : (
//               <span>View Tenders</span>
//             )}
//           </button>
//         </div>

//         <div onClick={handlePrint} className="cursor-pointer relative group">
//           <IoPrint size={25} />
//         </div>
//       </div>

//       {/* Additional Invoice Data Rendering */}
//       {loading && <div className="text-center mt-10">Loading...</div>}
//       {error && <div className="text-center mt-10 text-red-500">{error}</div>}
//       <div className="mt-6">
//         {/* Render invoice data here */}
//         {invoiceData.length > 0 ? (
//           <div
//             style={{ backgroundColor: '#F7F7F7' }}
//             className="max-w-4xl mx-auto bg-white shadow-md rounded-md p-6"
//           >
//             {/* Header Section */}
//             <div className="flex flex-col md:flex-row lg:flex-row justify-between items-center mb-6">
//               <div>
//                 <img
//                   src={tenderWiseLogo}
//                   alt="TableImg"
//                   className="rounded-lg h-auto 2xl:w-48 md:w-44 w-36"
//                 />
//               </div>
//               <div className="text-right">
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Invoice No: {invoiceData[0]?.invoice_no || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Date: {invoiceData[0]?.date_time || 'N/A'}
//                 </p>
//               </div>
//             </div>

//             {/* Customer Info */}
//             <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b-2">
//               Invoice To:
//             </h3>
//             <div className="mb-6 flex flex-col md:flex-row lg:flex-row justify-between">
//               <div>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Name: {invoiceData[0]?.name || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Phone: {invoiceData[0]?.phone_no || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Email: {invoiceData[0]?.email || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Address: {invoiceData[0]?.address || 'N/A'}
//                 </p>
//               </div>
//               <div>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Account Number: {invoiceData[0]?.card_number || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Transaction Id: {invoiceData[0]?.bank_trx_id || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Order Id: {invoiceData[0]?.order_id || 'N/A'}
//                 </p>
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600">
//                   Transaction Status: {invoiceData[0]?.bank_status || 'N/A'}
//                 </p>
//               </div>
//             </div>

//             {/* Payment Summary */}
//             <div className="mb-6">
//               <h3 className="text-lg font-semibold text-gray-800 pb-1 mb-1">
//                 Payment Summary:
//               </h3>
//               <div className="overflow-x-auto">
//                 <table className="w-full border-collapse border border-gray-300 text-sm">
//                   <thead className="bg-gray-200">
//                     <tr>
//                       <th className="border border-gray-300 px-4 py-2 text-left">
//                         Bank Status
//                       </th>
//                       <th className="border border-gray-300 px-4 py-2 text-center">
//                         Payable Amount
//                       </th>
//                       <th className="border border-gray-300 px-4 py-2 text-center">
//                         Received Amount
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {invoiceData.map((payment, index) => (
//                       <tr key={index}>
//                         <td className="border border-gray-300 px-4 py-2">
//                           {payment.method || 'N/A'}
//                         </td>
//                         <td className="border border-gray-300 px-4 py-2 text-center">
//                           {payment.payable_amount || '0.00'}
//                         </td>
//                         <td className="border border-gray-300 px-4 py-2 text-center">
//                           {payment.sp_message === 'cancelled' ||
//                           payment.sp_message === 'Failed'
//                             ? '0.00'
//                             : payment.recived_amount || '0.00'}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                   {/* Total Section */}
//                   {invoiceData.map((discount, index) => (
//                     <tfoot>
//                       <tr>
//                         <td
//                           className="px-4 py-2 text-right font-medium"
//                           colSpan="2"
//                         >
//                           Sub Total:
//                         </td>
//                         {/* <td className="px-4 py-2 text-center">{discount.amount}</td>
//                          */}
//                         <td className="px-4 py-2 text-center  text-gray-800">
//                           {discount.sp_message === 'cancelled' ||
//                           discount.sp_message === 'initiated' ||
//                           discount.sp_message === 'Failed'
//                             ? '0.00'
//                             : discount.amount || '0.00'}
//                         </td>
//                       </tr>

//                       <tr>
//                         <td
//                           className="px-4 py-2 text-right font-medium"
//                           colSpan="2"
//                         >
//                           Discount Percentage:
//                         </td>
//                         <td className="px-4 py-2 text-center">
//                           {discount.disc_percent}%
//                         </td>
//                       </tr>
//                       <tr>
//                         <td
//                           className="px-4 py-2 text-right font-medium"
//                           colSpan="2"
//                         >
//                           Discount Amount:
//                         </td>
//                         <td className="px-4 py-2 text-center">
//                           {discount.discount_amount}
//                         </td>
//                       </tr>
//                       <tr>
//                         <td
//                           className="px-4 py-2 text-right  text-gray-800"
//                           colSpan="2"
//                         >
//                           Grand Total:
//                         </td>
//                         <td className="px-4 py-2 text-center text-gray-800">
//                           {discount.sp_message === 'cancelled' ||
//                           discount.sp_message === 'Failed'
//                             ? '0.00'
//                             : discount.recived_amount || '0.00'}
//                         </td>
//                       </tr>
//                     </tfoot>
//                   ))}
//                 </table>
//               </div>
//             </div>

//             {invoiceData[0]?.method === 'CARD' && (
//               <div className="mb-6">
//                 <h3 className="text-lg font-semibold text-gray-800 pb-1 mb-1">
//                   Payment Summary:
//                 </h3>

//                 {/* Table */}
//                 <div className="overflow-x-auto">
//                   <table className="w-full border-collapse border border-gray-300 text-sm">
//                     {/* Separator Line with "Card Section" */}

//                     {/* Card Payment Table */}
//                     <thead className="bg-gray-200">
//                       <tr>
//                         <th className="border border-gray-300 px-4 py-2 text-left">
//                           Card Holder Name
//                         </th>
//                         <th className="border border-gray-300 px-4 py-2 text-left">
//                           USD amount
//                         </th>
//                         <th className="border border-gray-300 px-4 py-2 text-left">
//                           USD Rate
//                         </th>
//                         <th className="border border-gray-300 px-4 py-2 text-center">
//                           Payable Amount
//                         </th>
//                         <th className="border border-gray-300 px-4 py-2 text-center">
//                           Received Amount
//                         </th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {invoiceData.map((payment, index) => (
//                         <tr key={index}>
//                           <td className="border border-gray-300 px-4 py-2">
//                             {payment.card_holder_name || 'N/A'}
//                           </td>
//                           <td className="border border-gray-300 px-4 py-2 text-center">
//                             {payment.usd_amt || '0.00'}
//                           </td>
//                           <td className="border border-gray-300 px-4 py-2 text-center">
//                             {payment.usd_rate || '0.00'}
//                           </td>
//                           <td className="border border-gray-300 px-4 py-2 text-center">
//                             {payment.sp_message === 'cancelled' ||
//                             payment.sp_message === 'initiated' ||
//                             payment.sp_message === 'Failed'
//                               ? '0.00'
//                               : payment.recived_amount || '0.00'}
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>

//                     {/* Total Section */}
//                     {invoiceData.map((discount, index) => (
//                       <tfoot>
//                         <tr>
//                           <td
//                             className="px-4 py-2 text-right font-medium"
//                             colSpan="2"
//                           >
//                             Sub Total:
//                           </td>
//                           <td className="px-4 py-2 text-center  text-gray-800">
//                             {discount.sp_message === 'cancelled' ||
//                             discount.sp_message === 'Failed'
//                               ? '0.00'
//                               : discount.amount || '0.00'}
//                           </td>
//                         </tr>

//                         <tr>
//                           <td
//                             className="px-4 py-2 text-right font-medium"
//                             colSpan="2"
//                           >
//                             Discount Percentage:
//                           </td>
//                           <td className="px-4 py-2 text-center">
//                             {discount.disc_percent}%
//                           </td>
//                         </tr>
//                         <tr>
//                           <td
//                             className="px-4 py-2 text-right font-medium"
//                             colSpan="2"
//                           >
//                             Discount Amount:
//                           </td>
//                           <td className="px-4 py-2 text-center">
//                             {discount.discount_amount}
//                           </td>
//                         </tr>
//                         <tr>
//                           <td
//                             className="px-4 py-2 text-right  text-gray-800"
//                             colSpan="2"
//                           >
//                             Grand Total:
//                           </td>
//                           <td className="px-4 py-2 text-center text-gray-800">
//                             {discount.sp_message === 'cancelled' ||
//                             discount.sp_message === 'Failed'
//                               ? '0.00'
//                               : discount.recived_amount || '0.00'}
//                           </td>
//                         </tr>
//                       </tfoot>
//                     ))}
//                   </table>
//                 </div>
//               </div>
//             )}

//             {/* Footer Section */}
//             <div>
//               <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b-2">
//                 Thank You:
//               </h3>
//               <div className="text-center mt-2">
//                 <p className="md:text-base lg:text-lg text-sm text-gray-600 font-medium">
//                   Invoice was created on {invoiceData[0]?.date_time || 'N/A'}
//                 </p>
//               </div>
//             </div>
//           </div>
//         ) : (
//           <p className="text-center">No invoice data found.</p>
//         )}
//       </div>
//     </div>
//   );
// };

// export default InvoicePage;
